<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { usePresentLevelsStore } from './presentlevels.store'
import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import AddSubjectModal from './PresentlevelsAddSubjectModal.vue';
import PresentlevelsSubjectSummary from "./PresentlevelsSubjectSummary.vue";
import SubjectSummaryLoading from './SubjectSummaryLoading.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import PresentlevelsStudentHeader from './PresentlevelsStudentHeader.vue'
import PresentlevelsAlignedGoalsPanel from './PresentlevelsAlignedGoalsPanel.vue'
import PresentlevelsBrowseAssessmentsPanel from './PresentlevelsBrowseAssessmentsPanel.vue'
import ComposeIODButton from './ComposeIODButton.vue'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'

const presentLevelsStore = usePresentLevelsStore()
const contentProfileStore = useContentProfileStore()

const { plSession, enrolledGradeLabel, subjectSummaries, loadingSubjectSummaries } = storeToRefs(presentLevelsStore)
const { fetchingActiveProfile, activeProfile } = storeToRefs(contentProfileStore)

const studentName: ComputedRef<string> = computed(() => plSession.value?.student_name || '')

// Modal state
const showAddSubjectModal = ref(false)

// Track collapsed state for each summary
const collapsedSummaries = ref<Record<string, boolean>>({})

const handleSummaryCollapsed = (summaryUuid: string, isCollapsed: boolean) => {
  collapsedSummaries.value[summaryUuid] = isCollapsed
}

// Handler for adding a new subject
const handleAddSubject = async (subjectUUIDs: string[], subjectLabel: string) => {
  if (!plSession.value) {
    console.error('No session available to add subjects to')
    return
  }

  try {
    await presentLevelsStore.createSubjectSummaries({
      studentName: plSession.value.student_name,
      gradeUUID: plSession.value.enrolled_grade_uuid,
      subjectUUIDs: subjectUUIDs,
      sessionUUID: plSession.value.uuid
    })

    // Fetch categories and report upload options for the newly created subject summaries
    await Promise.all([
      presentLevelsStore.fetchCategoriesForSummarySubjects(),
      presentLevelsStore.fetchReportUploadOptions(contentProfileStore.activeProfile?.id)
    ])

    analyticsTracker.sendEvent(
      EVENT_NAMES.PRESENTLEVELS.SUMMARY.ADDED_AREA,
      { plw_added_subject: subjectLabel }
    )
  } catch (error) {
    console.error('Failed to add subjects:', error)
  }
}

const props = defineProps({
  session_uuid: {
    type: String,
    default: ''
  }
})

const sessionSubjects = computed(() => {
  return subjectSummaries.value?.map(s => s.subject_label)
})

const analyticsTracker = getAnalyticsTracker().getChildContext({
  plw_session_enrolled_grade: enrolledGradeLabel,
  plw_session_subjects: sessionSubjects,
})

function handleClickAddArea() {
  analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLICKED_ADD_AREA)
  showAddSubjectModal.value = true
}

async function handleCopySummary() {
  await presentLevelsStore.fetchSessionDetails(props.session_uuid, activeProfile.value?.id)
}

onMounted(async () => {
  // Wait for content profile to be loaded before proceeding
  if (fetchingActiveProfile.value || !activeProfile.value?.id) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(
        [fetchingActiveProfile, () => activeProfile.value?.id],
        ([fetching, profileId]) => {
          if (!fetching && profileId) {
            unwatch()
            resolve()
          }
        },
        { immediate: true }
      )
    })
  }
  
  const promises = [presentLevelsStore.fetchSessionDetails(props.session_uuid, activeProfile.value?.id)]
  if (!presentLevelsStore.levelOptions.length) {
    promises.push(presentLevelsStore.fetchLevelOptions())
  }
  if (!presentLevelsStore.subjectOptions.length) {
    promises.push(presentLevelsStore.fetchSubjectOptions())
  }
  await Promise.all(promises)
})
</script>

<template>
<main>
  <div class="flex justify-center">
    <div class="relative w-full min-w-0">
      <div class="flex items-start">
        <PresentlevelsStudentHeader
        :student-name="studentName"
        :grade-label="enrolledGradeLabel"
        />
      </div>
      <SubjectSummaryLoading v-if="loadingSubjectSummaries" />
      <div
        v-for="summary in subjectSummaries"
        :key="`subject-${summary.uuid}`"
        class="flex flex-col lg:flex-row items-start h-fit mb-5"
      >
        <PresentlevelsSubjectSummary
          :summary="summary"
          :category-options="presentLevelsStore.getCategoriesForSubject(summary.subject_uuid)"
          @copied="handleCopySummary"
          @collapsed="(isCollapsed) => handleSummaryCollapsed(summary.uuid, isCollapsed)"
        />
        <div class="flex flex-col">
          <PresentlevelsAlignedGoalsPanel
            :summaryUUID="summary.uuid"
            :parent-collapsed="collapsedSummaries[summary.uuid] || false"
          />
          <PresentlevelsBrowseAssessmentsPanel
            v-if="presentLevelsStore.getCategoriesForSubject(summary.subject_uuid)?.length"
            :browse-assessment-subject="presentLevelsStore.getCategoriesForSubject(summary.subject_uuid)?.[0]?.browse_assessment_subject || summary.subject_label"
          />
        </div>
      </div>
      <div v-if="!loadingSubjectSummaries" class="mt-8 hover:font-bold">
      <button
        @click="handleClickAddArea"
        class="w-full py-6 border border-dashed border-gray-700 rounded-lg bg-blue-200 transition-colors duration-200 flex items-center justify-center gap-1 text-gray-900 md:w-plw-subject-summary"
      >
          <span class="material-icons-round text-xl" aria-hidden="true">add</span>
          <span class="text-lg">Add Present Level Area</span>
        </button>
      </div>
    </div>
  </div>
  <AddSubjectModal
    v-model:visible="showAddSubjectModal"
    @add-subject="handleAddSubject"
  />
</main>
</template>

<style scoped>

</style>